// src/context/TourContext.tsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import Tour, { ReactourStep } from "reactour";
import { useNavigate } from "react-router-dom";

export type TourStep = {
  id: string;
  target: string; // selector (eg: '[data-tour="my-book-reviews"]') or 'body' for center
  content: React.ReactNode;
  selector?: string; // alias to target
  position?: 'top' | 'right' | 'bottom' | 'left' | 'center';
  order?: number;
  disableBeacon?: boolean;
  permission?: string;
};

type TourContextType = {
  registerStep: (step: TourStep) => void;
  unregisterStep: (id: string) => void;
  startTour: () => void;
  stopTour: () => void;
  isRunning: boolean;
  resetTour: () => void;
};

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
};

export const TourProvider: React.FC<{ children: React.ReactNode; runOnFirstVisit?: boolean }> = ({
  children,
  runOnFirstVisit = true,
}) => {
  const stepsRef = useRef<Map<string, TourStep>>(new Map());
  const [isRunning, setIsRunning] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const hasRunKey = "app_tour_shown_v1";

  const navigate=useNavigate();

  useEffect(() => {
    // alert(currentStepIndex);
    if(currentStepIndex==1)
      navigate(`${process.env.PUBLIC_URL}/book_review/template_list`);

  }, [currentStepIndex]);

  // build Reactour steps array from registered steps
  const buildSteps = (): ReactourStep[] => {
    const arr = Array.from(stepsRef.current.values())
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map<TourStep>((s) => s);

    const mapped: ReactourStep[] = arr.map((s) => {
      // Reactour uses `selector` field; if target is 'body' we'll set selector undefined and center it via styling
      const step: ReactourStep = {
        selector: s.target === "body" ? undefined : s.target,
        content: typeof s.content === "function" ? s.content : () => <div>{s.content}</div>,
        // Reactour 'position' is optional; we'll let it auto-place if not provided
      };
      return step;
    });

    return mapped;
  };

  const registerStep = (step: TourStep) => {
    stepsRef.current.set(step.id, step);
  };

  const unregisterStep = (id: string) => {
    stepsRef.current.delete(id);
  };

  const startTour = (startIndex = 0) => {
    // if we've already shown and runOnFirstVisit true, don't auto-run
    if (runOnFirstVisit && localStorage.getItem(hasRunKey)) return;
    setCurrentStepIndex(startIndex);
    setIsRunning(true);
  };

  const stopTour = () => {
    setIsRunning(false);
    localStorage.setItem(hasRunKey, "true");
  };

  const resetTour = () => {
    localStorage.removeItem(hasRunKey);
  };

  const value = useMemo(
    () => ({ registerStep, unregisterStep, startTour, stopTour, isRunning, resetTour }),
    [isRunning]
  );

  const reactourSteps = buildSteps();

  return (
    // <TourContext.Provider value={value}>
    //   {children}

    //   <Tour
    //   accentColor="#FF3500"
    //     steps={reactourSteps}
    //     isOpen={isRunning}
    //     onRequestClose={() => {
    //       stopTour();
    //     }}
    //     getCurrentStep={(i) => setCurrentStepIndex(i ?? 0)}
    //     // className="app-tour"
    //     // maskClassName="app-tour-mask"

    //     rounded={6}
    //     // You can tweak maskOpacity, accentColor, or class names to match your theme
    //     // For an element-targeted step where selector isn't found, reactour will gracefully skip it.
    //     // For steps that had target 'body' (fallback), reactour will center content automatically.
        
    //     // lastStepNextMessage="Finish"
    //     showCloseButton
    //   />
    // </TourContext.Provider>

     <TourContext.Provider value={value}>
      {children}

      {/* Inline CSS — only injected while the tour is running so it won't interfere otherwise */}
      {isRunning && (
        <style>{`
          /* ONLY override the popover wrapper background & shadow; DO NOT change the mask here */
          .app-tour .reactour__popover,
          .app-tour .reactour__helper,
          .reactour__popover,
          .reactour__helper {
            background: transparent !important;
            box-shadow: none !important;
            padding: 0 !important;
            border: 0 !important;
          }

          /* If an inner content element exists, let your dark card show through */
          .app-tour .reactour__content,
          .reactour__content {
            background: transparent !important;
            padding: 0 !important;
          }

          /* Keep the default dark overlay (mask). This is explicit so if something changed it gets restored */
          .reactour__mask,
          .app-tour-mask {
            background: rgba(0,0,0,0.65) !important;
          }

          /* Controls/close button: keep them visible but no white backgrounds */
          .app-tour .reactour__controls,
          .app-tour .reactour__close,
          .reactour__controls,
          .reactour__close {
            background: transparent !important;
            box-shadow: none !important;
          }
        `}</style>
      )}

      <Tour
        accentColor="#FF3500"
        steps={reactourSteps}
        isOpen={isRunning}
        onRequestClose={() => {
          stopTour();
        }}
        getCurrentStep={(i) => setCurrentStepIndex(i ?? 0)}
        rounded={6}
        className="app-tour"          /* used by our inline styles */
        // maskClassName="app-tour-mask" /* used by our inline styles */
        showCloseButton
      />
    </TourContext.Provider>
  );
};


// // src/context/TourContext.tsx
// import React, { createContext, useContext, useMemo, useRef, useState } from "react";
// import Tour, { ReactourStep } from "reactour";

// export type TourStep = {
//   id: string;
//   target: string; // selector (eg: '[data-tour="my-book-reviews"]') or 'body' for center
//   content: React.ReactNode;
//   position?: 'top' | 'right' | 'bottom' | 'left' | 'center';
//   order?: number;
//   permission?: string;
// };

// type TourContextType = {
//   registerStep: (step: TourStep) => void;
//   unregisterStep: (id: string) => void;
//   startTour: () => void;
//   stopTour: () => void;
//   isRunning: boolean;
//   resetTour: () => void;
// };

// const TourContext = createContext<TourContextType | undefined>(undefined);

// export const useTour = () => {
//   const ctx = useContext(TourContext);
//   if (!ctx) throw new Error("useTour must be used within TourProvider");
//   return ctx;
// };

// export const TourProvider: React.FC<{ children: React.ReactNode; runOnFirstVisit?: boolean }> = ({
//   children,
//   runOnFirstVisit = true,
// }) => {
//   const stepsRef = useRef<Map<string, TourStep>>(new Map());
//   const [isRunning, setIsRunning] = useState(false);
//   const hasRunKey = "app_tour_shown_v1";

//   const buildSteps = (): ReactourStep[] => {
//     const arr = Array.from(stepsRef.current.values())
//       .filter(Boolean)
//       .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

//     return arr.map((s) => ({
//       // If target is "body" we set selector undefined so reactour centers the popup
//       selector: s.target === "body" ? undefined : s.target,
//       content: typeof s.content === "function" ? s.content : () => <div>{s.content}</div>,
//     }));
//   };

//   const registerStep = (step: TourStep) => {
//     stepsRef.current.set(step.id, step);
//   };

//   const unregisterStep = (id: string) => {
//     stepsRef.current.delete(id);
//   };

//   const startTour = () => {
//     if (runOnFirstVisit && localStorage.getItem(hasRunKey)) return;
//     setIsRunning(true);
//   };

//   const stopTour = () => {
//     setIsRunning(false);
//     localStorage.setItem(hasRunKey, "true");
//   };

//   const resetTour = () => {
//     localStorage.removeItem(hasRunKey);
//   };

//   const value = useMemo(
//     () => ({ registerStep, unregisterStep, startTour, stopTour, isRunning, resetTour }),
//     [isRunning]
//   );

//   const reactourSteps = buildSteps();

//   return (
//     <TourContext.Provider value={value}>
//       {children}

//       <Tour
//         steps={reactourSteps}
//         isOpen={isRunning}
//         onRequestClose={() => {
//           stopTour();
//         }}
//         // accentColor for focus ring/buttons — matches your ManageUsers orange accent
//         accentColor="#FF3500"
//         // small radius to match your UI
//         rounded={8}
//         // attach a small class so we can rely on reactour's default markup but style content via content JSX
//         maskClassName="app-tour-mask"
//         className="app-tour"
//         // reactour will skip steps whose selector is not found
//         showCloseButton
//       />
//     </TourContext.Provider>
//   );
// };
